// AI 노트 «홈»(클로바 묶음 F2b) — 최근 만든 노트 + 월 달력(노트 있는 날 점) + 고른 날의 노트.
// 필수 프레젠테이션: 노트 열기·새 노트·녹음·폴더 서랍은 MeetingsPage 가 props 로 준다.
// 데이터는 여기서 직접 읽는다 — 최근 = GET /api/meetings?scope=all&sort=created&order=desc 앞 20개,
// 머리 «전체 노트 검색»에 검색어가 있으면 같은 요청에 q 를 더해 전체 노트를 찾는다(결과는 자르지 않는다 · H2),
// 달력 = GET /api/meetings/calendar?month=YYYY-MM, 고른 날 = GET /api/meetings?scope=all&date=YYYY-MM-DD.
// 🔴 날짜는 **KST** 로 정한다(서버 calendar·date 가 KST 날짜다) — 브라우저 시간대를 쓰지 않는다.
// 🔴 노트 행에 `article.gw-mnote` 를 쓰지 않는다(e2e 의 노트 목록 행 선택자 범위를 지킨다).
//    루트도 <section> 이 아니다(e2e 가 `section > div:first-child …` 로 목록 머리 제목을 읽는다).
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { IconFolder, IconMicrophone, IconPlus, IconSearch } from '@tabler/icons-react';
import { meetingAPI } from '../../api/client';
import { MEETING_THEME, cleanSearchText, fmtListDate, fmtListDur, parseKST, smBtn } from '../../utils/meetingUtil';
import { buildMonthCells } from '../supportboard/CalendarGrid';

const T = MEETING_THEME;
const KST_OFFSET_MS = 9 * 3600 * 1000;
const RECENT_MAX = 20;
const SEARCH_DELAY_MS = 300;   // 목록 검색(MeetingsPage G1)과 같은 디바운스
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const pad2 = (n) => String(n).padStart(2, '0');

// 지금의 KST 날짜 'YYYY-MM-DD' — UTC 에 9시간을 더한 뒤 getUTC* 로 읽는다(브라우저 시간대 무관).
export function kstToday(now = Date.now()) {
  const d = new Date(now + KST_OFFSET_MS);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

// 서버 시각(UTC naive) → KST «오전/오후 h:mm».
export function fmtKstTime(value) {
  const ms = parseKST(value ? String(value) : '');
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const d = new Date(ms + KST_OFFSET_MS);
  const hour = d.getUTCHours();
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour < 12 ? '오전' : '오후'} ${h12}:${pad2(d.getUTCMinutes())}`;
}

const monthOf = (key) => ({ year: Number(key.slice(0, 4)), month: Number(key.slice(5, 7)) - 1 });   // month 0부터
const monthKeyOf = (view) => `${view.year}-${pad2(view.month + 1)}`;
const dayLabel = (key) => `${Number(key.slice(5, 7))}월 ${Number(key.slice(8, 10))}일`;

export default function MeetingHome({
  mobile, openMeeting, onOpenFolders, onNewNote, startRec, disabledWork, reloadKey,
  initialQuery, onQueryChange,
}) {
  const today = kstToday();                                     // 오늘 칸 테두리 — 그릴 때마다 다시 본다(자정 넘김)
  const [view, setView] = useState(() => monthOf(kstToday()));  // 보고 있는 달(year, month 0부터)
  const [selDate, setSelDate] = useState(() => kstToday());     // 고른 날(처음엔 오늘)
  const [recent, setRecent] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [recentFailed, setRecentFailed] = useState(false);
  // 홈 검색(H2) — homeQuery = 입력값 · searchQ = 요청에 싣는 값(300ms 디바운스 뒤 정리한 값)
  // · recentQ = 지금 상자에 보이는 결과를 받아 온 요청의 검색어(제목·빈 문구·실패 문구·모바일 순서 기준).
  // 🔴 제목 등을 searchQ 로 고르지 않는다 — 요청이 나가기도 전에 «검색 결과가 없습니다.»가 번쩍인다.
  // 🔴 searchQ·recentQ 는 처음부터 정리한 initialQuery 다 — 노트를 열었다 닫아 다시 마운트되면
  //    첫 요청이 곧 그 검색이다(300ms 동안 최근 목록을 먼저 읽지 않고, 상자 제목도 처음부터 «검색 결과»).
  const [homeQuery, setHomeQuery] = useState(() => initialQuery || '');
  const [searchQ, setSearchQ] = useState(() => cleanSearchText(initialQuery));
  const [recentQ, setRecentQ] = useState(() => cleanSearchText(initialQuery));
  const mobileScrollRef = useRef(null);
  const [cal, setCal] = useState({ month: '', days: {} });     // 응답의 달과 함께 둔다 — 다른 달 점이 남지 않게
  const [calError, setCalError] = useState('');
  const [dayNotes, setDayNotes] = useState([]);
  const [dayLoading, setDayLoading] = useState(true);
  const [dayError, setDayError] = useState('');
  const monthKey = monthKeyOf(view);

  // 검색 — 입력이 300ms 멈추면 정리한 값이 **달라졌을 때만** searchQ 를 바꾼다
  // (같은 값이면 타이머도 걸지 않는다 — 빈 검색·공백만 입력에서 최근 목록을 한 번 더 읽지 않는다).
  useEffect(() => {
    const next = cleanSearchText(homeQuery);
    if (next === searchQ) return undefined;
    const t = setTimeout(() => setSearchQ(next), SEARCH_DELAY_MS);
    return () => clearTimeout(t);
  }, [homeQuery, searchQ]);

  // 🔴 세 읽기 모두 reloadKey(녹음·업로드 완료 = RecordingContext uploadVersion)에 다시 돈다 —
  //    녹음은 홈이 다시 마운트된 **뒤에** 업로드가 끝나므로 마운트만으로는 새 노트를 못 본다.
  // 🔴 늦게 온 이전 요청의 응답은 버린다(cancelled 가드 — 달·날짜를 빨리 넘기면 순서가 뒤바뀐다).
  // 최근 읽기는 검색어에 따라 가른다 — 빈 검색 = 지금과 같은 요청 + 앞 20개 · 검색 = q 를 더하고 자르지 않는다
  // (앞 20개로 자르면 21번째 이후 노트를 «없다»로 오해한다).
  useEffect(() => {
    let cancelled = false;
    const q = searchQ;
    setRecentFailed(false);
    setRecentLoading(true);
    const opts = { scope: 'all', sort: 'created', order: 'desc' };
    meetingAPI.list(q ? { ...opts, q } : opts)
      .then((r) => {
        if (cancelled) return;
        const rows = Array.isArray(r?.data) ? r.data : [];
        setRecent(q ? rows : rows.slice(0, RECENT_MAX));
        setRecentQ(q);
      })
      .catch(() => { if (!cancelled) { setRecent([]); setRecentQ(q); setRecentFailed(true); } })
      .finally(() => { if (!cancelled) setRecentLoading(false); });
    return () => { cancelled = true; };
  }, [reloadKey, searchQ]);

  // 모바일은 검색 결과 상자를 맨 위로 올린다 — 아래로 내린 채 검색하면 위로 간 결과가 화면 밖일 수 있어
  // 결과의 검색어가 바뀔 때마다 스크롤을 맨 위로 돌린다(같은 q 로 다시 읽기는 해당 없음).
  useEffect(() => {
    if (mobile && recentQ && mobileScrollRef.current) mobileScrollRef.current.scrollTop = 0;
  }, [mobile, recentQ]);

  useEffect(() => {
    let cancelled = false;
    setCalError('');
    meetingAPI.calendar(monthKey)
      .then((r) => {
        if (cancelled) return;
        const days = r?.data?.days;
        setCal({ month: monthKey, days: days && typeof days === 'object' ? days : {} });
      })
      .catch(() => {
        // 실패(서버 연도 범위 2000~2100 밖 400 포함)면 점 없이 달력만 둔다.
        if (cancelled) return;
        setCal({ month: monthKey, days: {} });
        setCalError('달력을 불러오지 못했습니다.');
      });
    return () => { cancelled = true; };
  }, [monthKey, reloadKey]);

  useEffect(() => {
    let cancelled = false;
    setDayError('');
    setDayLoading(true);
    setDayNotes([]);
    meetingAPI.list({ scope: 'all', date: selDate })
      .then((r) => { if (!cancelled) setDayNotes(Array.isArray(r?.data) ? r.data : []); })
      .catch(() => { if (!cancelled) { setDayNotes([]); setDayError('이 날의 노트를 불러오지 못했습니다.'); } })
      .finally(() => { if (!cancelled) setDayLoading(false); });
    return () => { cancelled = true; };
  }, [selDate, reloadKey]);

  const cells = useMemo(() => buildMonthCells(view.year, view.month), [view.year, view.month]);
  const days = cal.month === monthKey ? cal.days : {};
  const moveMonth = (delta) => setView((v) => {
    const m = v.month + delta;
    if (m < 0) return { year: v.year - 1, month: 11 };
    if (m > 11) return { year: v.year + 1, month: 0 };
    return { year: v.year, month: m };
  });
  const goToday = () => {
    const key = kstToday();
    setView(monthOf(key));
    setSelDate(key);
  };

  // 모바일 [녹음][새 노트] — MeetingList 의 noteActBtn 과 같은 값(시안 F .btn · 높이 31 · r5 · 13/500 · 그림자).
  // 녹음 = 보조(흰 바탕) · 새 노트 = 주(파랑) — e2e(test_pages_smoke·b20b)가 색을 단언한다.
  const noteActBtn = {
    height: '31px',
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '0 11px',
    borderRadius: T.radius.md,
    boxShadow: T.shadow.card,
    fontSize: T.font.base,
    fontWeight: 500,
    flex: '1 1 122px',
    opacity: disabledWork ? 0.5 : 1,
    cursor: disabledWork ? 'not-allowed' : 'pointer',
  };
  const iconSize = 15;
  const boxStyle = {
    boxSizing: 'border-box',
    minWidth: 0,
    background: T.color.surface,
    border: `1px solid ${T.color.border}`,
    borderRadius: T.radius.lg,
    boxShadow: T.shadow.card,
    padding: '12px',
  };
  const boxTitle = { fontSize: T.font.md, fontWeight: 620, letterSpacing: '-0.18px', color: T.color.textStrong, marginBottom: '8px' };
  const infoText = { color: T.color.muted, fontSize: T.font.sm, textAlign: 'center', padding: '14px 8px' };
  const errorText = { color: '#991b1b', background: T.color.dangerSoft, border: '1px solid #fecaca', borderRadius: T.radius.md, fontSize: T.font.sm, textAlign: 'center', padding: '10px', marginTop: '8px' };
  const rowBtn = {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    minWidth: 0,
    textAlign: 'left',
    padding: '9px 10px',
    marginBottom: '4px',
    border: `1px solid ${T.color.borderSoft}`,
    borderRadius: T.radius.md,
    background: T.color.surface,
    color: T.color.text,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
  const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };
  // 달력 ◀ ▶ «오늘» — 시안 F 보조 단추 모양(r5·500·그림자). 높이 28 은 달력 줄 배치 때문에 유지한다.
  // hover 바탕은 클래스 gw-mmenu-btn(아래 루트의 <style>)이 준다 — 인라인 background 를 이기려면 !important.
  const navBtn = {
    height: '28px',
    minWidth: '28px',
    padding: '0 8px',
    border: `1px solid ${T.color.border}`,
    borderRadius: T.radius.md,
    background: T.color.surface,
    boxShadow: T.shadow.card,
    color: T.color.text,
    fontSize: T.font.sm,
    fontWeight: 500,
    cursor: 'pointer',
    flexShrink: 0,
  };

  // 검색칸 — 🔴 자리표시는 목록 검색칸(«제목·전사·요약 검색»)과 다르다(시험·e2e 가 그 자리표시로 목록 칸을 찾는다).
  //    🔴 모바일은 화면 폭 안에(100% · border-box) — e2e 가 모바일 홈 가로 넘침을 잰다.
  const searchBox = (
    <div style={mobile
      ? { position: 'relative', width: '100%', boxSizing: 'border-box', minWidth: 0, marginBottom: '10px' }
      : { position: 'relative', flex: '0 1 280px', minWidth: 0 }}>
      <IconSearch size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: T.color.muted, pointerEvents: 'none' }} />
      <input
        value={homeQuery}
        onChange={(e) => { setHomeQuery(e.target.value); onQueryChange?.(e.target.value); }}
        placeholder="전체 노트 검색"
        aria-label="전체 노트 검색"
        maxLength={100}
        style={{ width: '100%', minWidth: 0, height: '36px', boxSizing: 'border-box', padding: '0 10px 0 32px', border: `1px solid ${T.color.border}`, borderRadius: T.radius.md, background: T.color.surface, color: T.color.text, fontSize: T.font.sm, fontWeight: 400, fontFamily: 'inherit', outline: 'none' }}
      />
    </div>
  );

  // 제목·빈 문구·실패 문구는 그 결과를 받아 온 요청의 검색어(recentQ)로 고른다.
  const recentTitle = recentQ ? '검색 결과' : '최근 만든 노트';
  const recentEmpty = recentQ ? '검색 결과가 없습니다.' : '아직 노트가 없습니다.';
  const recentError = recentFailed ? (recentQ ? '검색 결과를 불러오지 못했습니다.' : '최근 노트를 불러오지 못했습니다.') : '';
  const recentBox = (
    <div data-testid="meeting-home-recent" style={boxStyle}>
      <div style={boxTitle}>{recentTitle}</div>
      {recentLoading && recent.length === 0 && <div style={infoText}>불러오는 중…</div>}
      {recentError && <div role="alert" style={errorText}>{recentError}</div>}
      {!recentLoading && !recentError && recent.length === 0 && <div style={infoText}>{recentEmpty}</div>}
      {!recentError && recent.map((m) => {
        const date = fmtListDate(m.created_at);
        const dur = fmtListDur(m.duration_sec);
        return (
          <button key={m.id} type="button" onClick={() => openMeeting(m)} style={rowBtn}>
            <div style={{ ...ellipsis, fontSize: T.font.base, fontWeight: 800 }}>{m.title || '(제목 없음)'}</div>
            <div style={{ ...ellipsis, marginTop: '3px', fontSize: T.font.xs, color: T.color.muted }}>
              {[date, dur].filter(Boolean).join(' · ') || '-'}
            </div>
          </button>
        );
      })}
    </div>
  );

  const calendarBox = (
    <div data-testid="meeting-home-calendar" style={boxStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', minWidth: 0 }}>
        <span data-testid="meeting-home-month" style={{ ...ellipsis, flex: '1 1 auto', fontSize: T.font.md, fontWeight: 620, letterSpacing: '-0.18px', color: T.color.textStrong }}>
          {view.year}년 {view.month + 1}월
        </span>
        <button type="button" className="gw-mmenu-btn" aria-label="이전 달" title="이전 달" onClick={() => moveMonth(-1)} style={navBtn}>◀</button>
        <button type="button" className="gw-mmenu-btn" aria-label="다음 달" title="다음 달" onClick={() => moveMonth(1)} style={navBtn}>▶</button>
        <button type="button" className="gw-mmenu-btn" onClick={goToday} style={navBtn}>오늘</button>
      </div>
      {/* 🔴 고정 최소 폭 없이 7칸을 나눈다 — 375px 화면에서 가로로 넘치면 안 된다(CalendarGrid 의 minWidth 700 금지). */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', width: '100%', gap: '2px' }}>
        {WEEKDAYS.map((w, i) => (
          <div key={w} aria-hidden="true" style={{ textAlign: 'center', fontSize: T.font.xs, fontWeight: 700, padding: '2px 0 4px', color: i === 0 ? T.color.danger : T.color.muted }}>{w}</div>
        ))}
        {cells.map((c) => {
          const label = `${c.month + 1}월 ${c.day}일`;
          if (!c.inCurrentMonth) {
            return (
              <button key={c.date} type="button" disabled aria-label={label}
                style={{ minWidth: 0, minHeight: '34px', padding: 0, border: '1px solid transparent', borderRadius: T.radius.md, background: 'transparent', color: T.color.faint, opacity: 0.45, fontSize: T.font.sm, cursor: 'default' }}>
                {c.day}
              </button>
            );
          }
          const count = Number(days[c.date]) || 0;
          const isToday = c.date === today;
          const picked = c.date === selDate;
          return (
            <button key={c.date} type="button" onClick={() => setSelDate(c.date)}
              aria-label={count > 0 ? `${label} · 노트 ${count}개` : label}
              aria-pressed={picked}
              aria-current={isToday ? 'date' : undefined}
              style={{
                minWidth: 0,
                minHeight: '34px',
                padding: '2px 0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2px',
                border: `1px solid ${isToday ? T.color.green : 'transparent'}`,
                borderRadius: T.radius.md,
                background: picked ? T.color.green : 'transparent',
                color: picked ? '#fff' : T.color.text,
                fontSize: T.font.sm,
                fontWeight: picked || isToday ? 800 : 600,
                cursor: 'pointer',
              }}>
              <span>{c.day}</span>
              <span data-testid={count > 0 ? 'meeting-home-dot' : undefined} aria-hidden="true"
                style={{ width: '5px', height: '5px', borderRadius: '50%', background: count > 0 ? (picked ? '#fff' : T.color.green) : 'transparent' }} />
            </button>
          );
        })}
      </div>
      {calError && <div role="alert" style={errorText}>{calError}</div>}
    </div>
  );

  const dayBox = (
    <div data-testid="meeting-home-day" style={boxStyle}>
      <div style={boxTitle}>{dayLabel(selDate)}</div>
      <div style={{ maxHeight: '240px', overflowY: 'auto', minWidth: 0 }}>
        {dayLoading && dayNotes.length === 0 && <div style={infoText}>불러오는 중…</div>}
        {dayError && <div role="alert" style={errorText}>{dayError}</div>}
        {!dayLoading && !dayError && dayNotes.length === 0 && <div style={infoText}>노트가 없습니다.</div>}
        {!dayError && dayNotes.map((m) => (
          <button key={m.id} type="button" onClick={() => openMeeting(m)}
            style={{ ...rowBtn, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ ...ellipsis, flex: '1 1 auto', fontSize: T.font.base, fontWeight: 700 }}>{m.title || '(제목 없음)'}</span>
            <span style={{ flexShrink: 0, fontSize: T.font.xs, color: T.color.muted, fontVariantNumeric: 'tabular-nums' }}>🕐 {fmtKstTime(m.created_at)}</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="meeting-home" style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', background: T.color.page }}>
      {/* 달력 단추 hover — MeetingList 의 같은 규칙은 목록이 그려질 때만 있으니 홈도 자기 것을 둔다.
          🔴 루트의 첫 자식이어야 한다(마지막 자식 = 스크롤 칸 자리를 바꾸지 않는다). */}
      <style>{`.gw-mmenu-btn:hover:not(:disabled){background:var(--gw-hover,#efefed)!important}`}</style>
      <div style={{ flexShrink: 0, padding: mobile ? '12px' : '14px', borderBottom: `1px solid ${T.color.borderSoft}`, background: T.color.surface }}>
        <div style={{ fontWeight: 800, fontSize: T.font.lg, display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, marginBottom: mobile ? '10px' : 0 }}>
          {mobile && <button type="button" onClick={onOpenFolders} title="폴더 목록" style={{ ...smBtn(T.color.slate), padding: '5px 9px', display: 'inline-flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}><IconFolder size={iconSize} />폴더</button>}
          <span style={{ ...ellipsis, flex: '1 1 auto', fontSize: T.font.md, fontWeight: 620, letterSpacing: '-0.18px' }}>홈</span>
          {!mobile && searchBox}
        </div>
        {mobile && searchBox}
        {mobile && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button type="button" onClick={startRec} disabled={disabledWork} style={{ ...noteActBtn, background: T.color.surface, color: T.color.text, border: `1px solid ${T.color.border}` }}><IconMicrophone size={iconSize} />녹음</button>
            <button type="button" onClick={onNewNote} disabled={disabledWork} style={{ ...noteActBtn, background: T.color.green, color: 'white', border: `1px solid ${T.color.green}` }}><IconPlus size={iconSize} />새 노트</button>
          </div>
        )}
      </div>
      {mobile ? (
        // 모바일 = 한 줄로 쌓는다(달력 → 고른 날 → 최근). 🔴 alignItems:'stretch' — 'flex-start' 면 상자 폭이
        //   내용 길이로 정해져 긴 제목이 말줄임 없이 가로로 넘친다. 하단 탭바 여백은 gw-tabbar-pad.
        //   검색 결과(recentQ)가 있으면 결과 상자를 맨 위로 올린다(맨 아래면 검색해도 결과가 화면 밖이다).
        //   key 를 줘서 순서가 바뀌어도 상자를 옮길 뿐 다시 만들지 않는다.
        <div ref={mobileScrollRef} className="gw-tabbar-pad" style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '12px', padding: '12px' }}>
          {(recentQ
            ? [['recent', recentBox], ['calendar', calendarBox], ['day', dayBox]]
            : [['calendar', calendarBox], ['day', dayBox], ['recent', recentBox]]
          ).map(([key, box]) => <Fragment key={key}>{box}</Fragment>)}
        </div>
      ) : (
        // 데스크톱 = 왼쪽 최근 노트 · 오른쪽 달력+고른 날. 좁으면 달력 열이 아래로 내려간다(wrap).
        <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', alignContent: 'flex-start', gap: '16px', padding: '16px' }}>
          <div style={{ flex: '1 1 320px', minWidth: 0 }}>{recentBox}</div>
          <div style={{ flex: '0 1 340px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {calendarBox}
            {dayBox}
          </div>
        </div>
      )}
    </div>
  );
}
